import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LEAVE_STATUSES,
  LEAVE_STATUS_MAP,
  LEAVE_TYPES,
  LEAVE_TYPE_MAP,
  type LeaveStatus,
  type LeaveType,
} from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { useListState } from '../../lib/hooks';
import { date } from '../../lib/format';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  PageHead,
  Pager,
  Stat,
  TableSkeleton,
} from '../../components/ui';

interface LeaveRow {
  id: string;
  employeeId: string;
  employeeName: string;
  department: string | null;
  type: LeaveType;
  status: LeaveStatus;
  fromDate: string;
  toDate: string;
  days: string;
  reason: string | null;
  decisionNote: string | null;
  balance: string | null;
}

interface LeavePage {
  items: LeaveRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

export function LeavesList() {
  const { state, patch, page } = useListState();
  const [requesting, setRequesting] = useState(false);
  const [deciding, setDeciding] = useState<LeaveRow | null>(null);

  const query = { ...state, page };

  const list = useQuery({
    queryKey: ['leaves', query],
    queryFn: () => api<LeavePage>(`/hr/leaves${qs(query)}`),
  });

  const rows = list.data?.items ?? [];
  const pending = rows.filter((l) => l.status === 'PENDING').length;

  return (
    <div className="page">
      <PageHead
        title="الإجازات"
        subtitle={list.data ? `${list.data.total} طلبًا` : undefined}
        actions={
          <button className="btn btn-primary" onClick={() => setRequesting(true)}>
            طلب إجازة
          </button>
        }
      />

      <div className="stats">
        <Stat label="قيد الاعتماد" value={pending} />
        <Stat
          label="معتمدة في هذه الصفحة"
          value={rows.filter((l) => l.status === 'APPROVED').length}
        />
      </div>

      <div className="toolbar">
        <select
          className="select"
          value={state.status ?? ''}
          onChange={(e) => patch({ status: e.target.value || undefined })}
        >
          <option value="">كل الحالات</option>
          {LEAVE_STATUSES.map((s) => (
            <option key={s} value={s}>
              {LEAVE_STATUS_MAP[s].label}
            </option>
          ))}
        </select>

        <select
          className="select"
          value={state.type ?? ''}
          onChange={(e) => patch({ type: e.target.value || undefined })}
        >
          <option value="">كل الأنواع</option>
          {LEAVE_TYPES.map((t) => (
            <option key={t} value={t}>
              {LEAVE_TYPE_MAP[t].label}
            </option>
          ))}
        </select>
      </div>

      {list.isError && <ErrorState error={list.error} onRetry={() => list.refetch()} />}

      <div className="card">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>الموظف</th>
                <th>النوع</th>
                <th>من</th>
                <th>إلى</th>
                <th>الأيام</th>
                <th>الحالة</th>
                <th />
              </tr>
            </thead>
            {list.isLoading && <TableSkeleton rows={6} cols={7} />}

            <tbody>
              {rows.map((l) => (
                <tr key={l.id}>
                  <td>
                    <span className="cell-strong">{l.employeeName}</span>
                    {l.reason && <div className="tiny mute">{l.reason}</div>}
                  </td>
                  <td>
                    <Badge kind={LEAVE_TYPE_MAP[l.type].kind}>
                      {LEAVE_TYPE_MAP[l.type].label}
                    </Badge>
                  </td>
                  <td className="num cell-mute">{date(l.fromDate)}</td>
                  <td className="num cell-mute">{date(l.toDate)}</td>
                  <td className="num">{Number(l.days)}</td>
                  <td>
                    <Badge kind={LEAVE_STATUS_MAP[l.status].kind}>
                      {LEAVE_STATUS_MAP[l.status].label}
                    </Badge>
                  </td>
                  <td>
                    <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                      {l.status === 'PENDING' && (
                        <button className="btn btn-sm btn-primary" onClick={() => setDeciding(l)}>
                          قرار
                        </button>
                      )}
                      {(l.status === 'PENDING' || l.status === 'APPROVED') && (
                        <CancelButton leave={l} />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!list.isLoading && rows.length === 0 && (
          <EmptyState title="لا طلبات إجازة" text="تظهر هنا طلبات الفريق لاعتمادها." />
        )}

        {list.data && (
          <Pager
            page={page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => patch({ page: String(p) })}
          />
        )}
      </div>

      {requesting && <RequestDialog onClose={() => setRequesting(false)} />}
      {deciding && <DecideDialog leave={deciding} onClose={() => setDeciding(null)} />}
    </div>
  );
}

function RequestDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [employeeId, setEmployeeId] = useState('');
  const [type, setType] = useState<LeaveType>('ANNUAL');
  const [fromDate, setFrom] = useState(new Date().toISOString().slice(0, 10));
  const [toDate, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');

  const employees = useQuery({
    queryKey: ['employees', { pageSize: 200, status: 'ACTIVE' }],
    queryFn: () =>
      api<{ items: Array<{ id: string; name: string; annualLeaveBalance: string }> }>(
        '/hr/employees?pageSize=200&status=ACTIVE',
      ),
  });

  const create = useMutation({
    mutationFn: () =>
      api('/hr/leaves', {
        method: 'POST',
        body: { employeeId, type, fromDate, toDate, reason: reason || null },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['leaves'] });
      onClose();
    },
  });

  const selected = employees.data?.items.find((e) => e.id === employeeId);
  const valid = employeeId && fromDate <= toDate;

  return (
    <Modal
      title="طلب إجازة"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => create.mutate()}
            disabled={!valid || create.isPending}
          >
            {create.isPending ? 'جارٍ الإرسال' : 'إرسال الطلب'}
          </button>
        </>
      }
    >
      {create.isError && (
        <div className="alert alert-danger mb-4">
          {create.error instanceof Error ? create.error.message : 'تعذّر الطلب.'}
        </div>
      )}

      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="l-employee">
            الموظف
          </label>
          <select
            id="l-employee"
            className="select"
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
          >
            <option value="">اختر</option>
            {employees.data?.items.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
          {selected && (
            <span className="tiny mute">
              رصيده المتبقي {Number(selected.annualLeaveBalance)} يومًا.
            </span>
          )}
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="l-type">
            النوع
          </label>
          <select
            id="l-type"
            className="select"
            value={type}
            onChange={(e) => setType(e.target.value as LeaveType)}
          >
            {LEAVE_TYPES.map((t) => (
              <option key={t} value={t}>
                {LEAVE_TYPE_MAP[t].label}
              </option>
            ))}
          </select>
          <span className="tiny mute">
            {LEAVE_TYPE_MAP[type].deductsBalance
              ? 'تُخصم من الرصيد السنوي عند الاعتماد.'
              : 'لا تُخصم من الرصيد السنوي.'}
            {!LEAVE_TYPE_MAP[type].paid && ' وتُخصم من المرتب.'}
          </span>
        </div>

        <div className="field">
          <label className="label req" htmlFor="l-from">
            من
          </label>
          <input
            id="l-from"
            className="input num"
            type="date"
            value={fromDate}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label req" htmlFor="l-to">
            إلى
          </label>
          <input
            id="l-to"
            className="input num"
            type="date"
            value={toDate}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="l-reason">
            السبب
          </label>
          <textarea
            id="l-reason"
            className="input"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </div>

      {/*
        الأيام تُحسب في الخادم لا هنا: العطلة الأسبوعية والرسمية تُستثنى،
        وحسابها في الواجهة يجعل الرقم المعروض مخالفًا لما يُخصم فعلًا.
      */}
      <p className="tiny mute mt-3">
        تُحسب أيام العمل وحدها: العطلة الأسبوعية والرسمية لا تُخصم من الرصيد.
      </p>
    </Modal>
  );
}

function DecideDialog({ leave, onClose }: { leave: LeaveRow; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');

  const decide = useMutation({
    mutationFn: (status: 'APPROVED' | 'REJECTED') =>
      api(`/hr/leaves/${leave.id}/decide`, {
        method: 'PATCH',
        body: { status, decisionNote: note || null },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['leaves'] });
      onClose();
    },
  });

  const days = Number(leave.days);
  const balance = leave.balance === null ? null : Number(leave.balance);
  const deducts = LEAVE_TYPE_MAP[leave.type].deductsBalance;
  const short = deducts && balance !== null && days > balance;

  return (
    <Modal
      title="قرار في طلب إجازة"
      onClose={onClose}
      footer={
        <>
          <button
            className="btn btn-danger"
            onClick={() => decide.mutate('REJECTED')}
            disabled={decide.isPending}
          >
            رفض
          </button>
          <button
            className="btn btn-primary"
            onClick={() => decide.mutate('APPROVED')}
            disabled={decide.isPending || short}
          >
            اعتماد
          </button>
        </>
      }
    >
      {decide.isError && (
        <div className="alert alert-danger mb-4">
          {decide.error instanceof Error ? decide.error.message : 'تعذّر القرار.'}
        </div>
      )}

      <div className="totals-box">
        <div className="totals-row">
          <span className="mute">الموظف</span>
          <span className="strong">{leave.employeeName}</span>
        </div>
        <div className="totals-row">
          <span className="mute">النوع</span>
          <span>{LEAVE_TYPE_MAP[leave.type].label}</span>
        </div>
        <div className="totals-row">
          <span className="mute">المدة</span>
          <span className="num">
            {date(leave.fromDate)} — {date(leave.toDate)}
          </span>
        </div>
        <div className="totals-row totals-final">
          <span>أيام العمل</span>
          <span className="num">{days}</span>
        </div>
      </div>

      {deducts && balance !== null && (
        <div className={`alert mt-4 ${short ? 'alert-danger' : 'alert-info'}`}>
          {short ? (
            <>
              رصيده {balance} يومًا ولا يكفي {days} أيام. لا يمكن الاعتماد — عدّل المدة أو
              اجعلها بدون أجر.
            </>
          ) : (
            <>
              رصيده الآن {balance} يومًا، ويصير {balance - days} بعد الاعتماد.
            </>
          )}
        </div>
      )}

      {leave.reason && (
        <div className="field mt-4">
          <span className="label">سبب الطلب</span>
          <span>{leave.reason}</span>
        </div>
      )}

      <div className="field mt-4">
        <label className="label" htmlFor="d-note">
          ملاحظة على القرار
        </label>
        <textarea
          id="d-note"
          className="input"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
    </Modal>
  );
}

/** الإلغاء يعيد ما خُصم من الرصيد إن كانت معتمدة. */
function CancelButton({ leave }: { leave: LeaveRow }) {
  const queryClient = useQueryClient();
  const cancel = useMutation({
    mutationFn: () => api(`/hr/leaves/${leave.id}/cancel`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['leaves'] }),
  });

  return (
    <button className="btn btn-sm" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
      إلغاء
    </button>
  );
}
